/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { Box, Grid } from "@mui/material";
import { UseFormReturn } from "react-hook-form";

import { useCurrentUserContext } from "../../../../common/contexts/CurrentUserContext";
import { isPhoneNumberCountryAllowed, isRequired } from "../../../../common/helpers/validators";
import { MfaLock } from "../../../../icons/illustration";
import { Mandatory2FAStepLayout } from "../Mandatory2FAStepLayout/Mandatory2FAStepLayout";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { usePhoneCountryNotAllowedErrorMessage } from "@/common/hooks/usePhoneCountryNotAllowedErrorMessage";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInputLabel } from "@/dashboard-design-system/InputLabel";
import { DashboardPhoneNumberInput } from "@/dashboard-design-system/PhoneNumberInput";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

type Mandatory2FAStepProps = {
  form: UseFormReturn<{ phoneNumber: string }, unknown, undefined>;
  isLoadingSendSms: boolean;
  isSendButtonDisabled: boolean;
  sendSmsCooldown: number;
  onSubmitForm: (e: React.FormEvent<HTMLFormElement>) => void;
  submitOnKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const Mandatory2FAStep = ({
  form,
  isLoadingSendSms,
  isSendButtonDisabled,
  sendSmsCooldown,
  onSubmitForm,
  submitOnKeyDown,
}: Mandatory2FAStepProps) => {
  const { t } = useTranslate();
  const getPhoneCountryErrorMessage = usePhoneCountryNotAllowedErrorMessage();
  const { organization } = useCurrentUserContext();
  const { isPhoneScreen } = useMediaQuery();

  return (
    <form onSubmit={onSubmitForm}>
      <Mandatory2FAStepLayout>
        <Box>
          <MfaLock className="svg-safari-fix" />
        </Box>
        <Grid item xs={12}>
          <DashboardTypography variant="h6">
            {t("dashboard", "settings.twoFactorAuthBySmsModal.setup.title")}
          </DashboardTypography>
        </Grid>
        <Grid item>
          <DashboardTypography variant="body2">
            {t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.description")}
          </DashboardTypography>
        </Grid>
        <Grid item container>
          <Grid item>
            <DashboardInputLabel label={t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.label")} />
          </Grid>
          <Grid item container spacing={1} alignItems="center">
            <Grid item xs>
              <DashboardPhoneNumberInput
                data-test="phone-number-field"
                name="phoneNumber"
                control={form.control}
                errorMessage={getPhoneCountryErrorMessage(form.formState.errors.phoneNumber?.message)}
                organizationCountry={organization?.country}
                onKeyDown={submitOnKeyDown}
                skipCountryValidation
                rules={[isRequired(t("common", "phoneNumber.required")), isPhoneNumberCountryAllowed()]}
              />
            </Grid>
            {!isPhoneScreen && (
              <Grid item alignSelf="start" marginTop="1px">
                <DashboardButton
                  type="submit"
                  vibe="brand"
                  variant="filled"
                  isLoading={isLoadingSendSms}
                  disabled={isSendButtonDisabled}
                  data-test="submit-phone-number-button"
                >
                  {sendSmsCooldown > 0
                    ? `${t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.send")} - ${sendSmsCooldown}s`
                    : t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.send")}
                </DashboardButton>
              </Grid>
            )}
          </Grid>
        </Grid>
        {isPhoneScreen && (
          <Grid item xs={12}>
            <DashboardButton
              type="submit"
              vibe="brand"
              variant="filled"
              isLoading={isLoadingSendSms}
              disabled={isSendButtonDisabled}
              fullWidth
              data-test="submit-phone-number-button"
            >
              {sendSmsCooldown > 0
                ? `${t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.send")} - ${sendSmsCooldown}s`
                : t("dashboard", "settings.twoFactorAuthBySmsModal.setup.phoneNumber.send")}
            </DashboardButton>
          </Grid>
        )}
      </Mandatory2FAStepLayout>
    </form>
  );
};
